import React from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { FiCopy, FiExternalLink, FiZap } from 'react-icons/fi';
import { copyTextToClipboard } from '../../blockchain/offlineSigning';
import { toCoreDisplayAddress } from '../../blockchain/coreIcanAddress';
import { useClientConfig } from '../../contexts/ClientConfigContext';

const Wrap = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  background: rgba(79, 172, 254, 0.06);
  border: 1px solid rgba(79, 172, 254, 0.22);
  border-radius: 8px;
  font-size: 0.85rem;
  .head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.35rem 0.65rem;
    margin-bottom: 0.35rem;
  }
  .label {
    color: #94a3b8;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.04em;
  }
  .addr {
    font-family: Consolas, Monaco, monospace;
    color: #e2e8f0;
    font-size: 0.82rem;
    word-break: break-all;
  }
  .meta {
    color: #64748b;
    font-size: 0.78rem;
    line-height: 1.4;
    margin-bottom: 0.5rem;
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
  }
`;

function shortenAddr(addr) {
  const a = String(addr || '').trim();
  if (a.length < 18) return a || '—';
  return `${a.slice(0, 10)}…${a.slice(-8)}`;
}

export function formatContractDeployedAt(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

/**
 * @param {{ assetClass: object, actionClassName: string, onDeployClick: () => void, canDeploy?: boolean }} props
 */
export function AssetClassContractCardBlock({
  assetClass,
  actionClassName,
  onDeployClick,
  canDeploy = true
}) {
  const { addressExplorerUrl, explorerConfigured } = useClientConfig();
  const addr = assetClass?.contractAddress?.trim();
  const displayAddr = addr ? toCoreDisplayAddress(addr) : '';

  const handleCopy = async () => {
    if (!displayAddr) return;
    await copyTextToClipboard(displayAddr);
    toast.success('Contract address copied');
  };

  return (
    <Wrap>
      <div className="head">
        <span className="label">Core contract</span>
      </div>
      {addr ? (
        <>
          <div className="addr" title={displayAddr}>
            {shortenAddr(displayAddr)}
          </div>
          <div className="meta">
            <span style={{ color: '#7dd3fc' }}>{assetClass.tokenStandard || '—'}</span>
            {formatContractDeployedAt(assetClass.contractDeployedAtUtc) && (
              <>
                <span> · </span>
                <span>Deployed {formatContractDeployedAt(assetClass.contractDeployedAtUtc)}</span>
              </>
            )}
          </div>
          <div className="actions">
            <button type="button" className={actionClassName} onClick={handleCopy}>
              <FiCopy /> Copy address
            </button>
            <button
              type="button"
              className={actionClassName}
              disabled={!explorerConfigured}
              title={explorerConfigured ? 'Open contract in block explorer' : 'Block explorer URL not loaded from API'}
              onClick={() => {
                const url = addressExplorerUrl(displayAddr || addr);
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              <FiExternalLink /> Block explorer
            </button>
          </div>
        </>
      ) : (
        <>
          {canDeploy ? (
            <>
              <div className="meta" style={{ marginBottom: '0.65rem' }}>
                No on-chain contract is linked to this asset class yet. Prepare an unsigned deployment with your offline
                signer, then submit the signed transaction.
              </div>
              <div className="actions">
                <button type="button" className={actionClassName} onClick={onDeployClick}>
                  <FiZap /> Deploy smart contract
                </button>
              </div>
            </>
          ) : (
            <div className="meta" style={{ marginBottom: 0 }}>
              No on-chain contract is linked yet. Platform standard asset classes are deployed by an administrator.
            </div>
          )}
        </>
      )}
    </Wrap>
  );
}
